import React, { useState, useContext, useCallback } from 'react'
import { MessageContext, StatusContext, DataRoomsContext } from './Contexts'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  FormGroup,
  TextField,
  FormControl,
  MenuItem,
  Typography,
} from '@mui/material'
import { AddRoom, subscribeToSessionChannel } from 'modules/Rest'
import ActionButtons from './ActionButtons'
import { setModes } from 'modules/Utils'
import { useNavigate } from 'react-router'
import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles({
  dialogContent: {
    width: 400,
    paddingTop: '10px !important',
  }
})

export default function AddDataRoomDialog({ mode, setMode, sourceDataRoom, onExited }) {
  const { messageDispatch, setNotificationBarOpen } = useContext(MessageContext)
  const { sessionStatus } = useContext(StatusContext)
  const navigate = useNavigate()
  const { triggerRefresh } = useContext(DataRoomsContext)
  const isCopy = !!sourceDataRoom
  const [searchable, setSearchable] = useState(true)
  const [searchable_content, setSearchable_content] = useState(false)
  const [two_factor_allowed, setTwo_factor_allowed] = useState(false)
  const [qa, setQA] = useState(false)
  const [billing_details, setBillingDetails] = useState('')
  const [data_room_public_name, setDataRoomPublicName] = useState('')
  const [data_room_name, setDataRoomName] = useState('')
  const [emptyNameError, setEmptyNameError] = useState()
  const [light, setLight] = useState(false)

  const classes = useStyles()

  const add = async () => {
    try {
      setMode('pending')
      const {
        data: { data_room, success, info, operation_id },
      } = await AddRoom({
        searchable,
        searchable_content,
        two_factor_allowed,
        qa,
        data_room_public_name,
        data_room_name,
        light,
        billing_details,
        source_data_room: sourceDataRoom?.data_room,
      })
      if (success) {
        if (operation_id) {
          messageDispatch({
            type: 'create operation',
            payload: {
              stage_label: 'Copying contents',
              data_room_display_name: data_room.data_room_public_name,
              data_room: data_room.data_room,
              operation_id,
            },
          })
          subscribeToSessionChannel(messageDispatch)
            .then(() => setNotificationBarOpen(true))
            .catch(() => {
              // The data room has already been created; only progress tracking failed.
            })
        }
        await setModes(setMode, ['completing', 1500, ''])
        triggerRefresh()
        navigate(`/data-room/${data_room.data_room}`)
      } else {
        throw new Error(info ? info : 'An error occurred')
      }
    } catch (error) {
      setMode('')
      messageDispatch({ type: 'warning', payload: error.message })
    }
  }

  const submit = () => {
    if(data_room_public_name) {
      add()
    } else {
      setEmptyNameError('Please enter a name for the room')
    }
  }

  // Reset / prefill the dialog state every time it opens. Runs in MUI's
  // transition onEnter hook so React state mirrors the freshly-opened dialog
  // regardless of whether we're creating from scratch or copying an existing
  // data room.
  const initDialog = useCallback(() => {
    if (sourceDataRoom) {
      const baseName = sourceDataRoom.data_room_public_name || sourceDataRoom.data_room_display_name || ''
      setDataRoomPublicName(baseName ? `${baseName} (copy)` : '')
      setDataRoomName(sourceDataRoom.data_room_name ? `${sourceDataRoom.data_room_name} (copy)` : '')
      setLight(!!sourceDataRoom.light ? !!sessionStatus.allow_light : !sessionStatus.allow_full)
    } else {
      setDataRoomPublicName('')
      setDataRoomName('')
      setLight(!sessionStatus.allow_full)
    }
    setSearchable(true)
    setSearchable_content(false)
    setTwo_factor_allowed(false)
    setQA(false)
    setBillingDetails('')
    setEmptyNameError(undefined)
  }, [sourceDataRoom, sessionStatus.allow_full, sessionStatus.allow_light])

  return (
    (<Dialog
      open={mode !== ''}
      onClose={() => setMode('')}
      slotProps={{
        transition: { onEnter: initDialog, onExited }
      }}
      disableRestoreFocus
    >
      <DialogTitle>{!isCopy ? 'Create new data room' : 'Create copy of data room'}</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <FormControl fullWidth>
          <TextField label='Data room type' variant='standard' select value={light} onChange={(event) => setLight(event.target.value)}>
            {sessionStatus.allow_full && <MenuItem value={false}>Standard</MenuItem>}
            {sessionStatus.allow_light && <MenuItem value={true}>Light</MenuItem>}
          </TextField>
        </FormControl>

        <TextField
          value={data_room_public_name}
          onChange={({target}) => setDataRoomPublicName(target.value)}
          onKeyPress={({key}) => key === 'Enter' && submit()}
          autoFocus
          margin='dense'
          label='Public name'
          type='text'
          error={!data_room_public_name && !!emptyNameError}
          helperText={!data_room_public_name && emptyNameError ? emptyNameError : ''}
          fullWidth
          variant='standard'
        />

        <TextField
          value={data_room_name}
          onChange={({ target }) => setDataRoomName(target.value)}
          onKeyPress={({key}) => key === 'Enter' && submit()}
          margin='dense'
          label='Private name (visible to administrators only)'
          type='text'
          fullWidth
          variant='standard'
        />

        {!light && <FormGroup>
          <FormControlLabel
            control={<Checkbox checked={searchable} onChange={(event) => setSearchable(event.target.checked)}/>}
            label='Enable file name search'
          />
          <FormControlLabel
            control={<Checkbox checked={searchable_content} onChange={(event) => setSearchable_content(event.target.checked)}/>}
            label='Enable PDF content search'
          />
          <FormControlLabel
            control={<Checkbox checked={two_factor_allowed} onChange={(event) => setTwo_factor_allowed(event.target.checked)}/>}
            label='Enable enforced two-factor authentication'
          />
          <FormControlLabel control={<Checkbox checked={qa} onChange={(event) => setQA(event.target.checked)}/>} label='Enable Q&A'/>
        </FormGroup>}

        {sessionStatus.show_billing_details && (
          <TextField
            label='Add billing details'
            multiline
            sx={{ marginTop: 1 }}
            rows={4}
            fullWidth
            variant='outlined'
            value={billing_details}
            onChange={({ target: { value } }) => setBillingDetails(value)}
          />
        )}

        {isCopy && (
          <Typography variant='body2' color='textSecondary' sx={{ mt: 1 }}>
            Only files and folders are copied. Settings and user rights are not copied.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <ActionButtons
          mode={mode}
          setMode={setMode}
          cancel={(e, setMode) => setMode('')}
          actionTitle={!isCopy ? 'Create new data room' : 'Create copy'}
          action={submit}
        />
      </DialogActions>
    </Dialog>)
  );
}
